// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import {
  ChevronRight,
  Eye,
  EyeOff,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { InlineDeleteConfirm } from '@/app/components/inline-delete-confirm';
import { BulkActionBar, SelectionCheckbox } from '@/app/components/bulk-action-bar';
import { PageHeader, FormCard } from '@/app/components/page-layout';
import { UnsavedChangesGuard } from '@/app/components/unsaved-changes-guard';
import type { BulkAction } from '@/app/components/bulk-action-bar';
import { useBulkSelection } from '@/app/hooks/useBulkSelection';
import { useUnsavedChanges } from '@/app/hooks/useUnsavedChanges';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  useCustomSections,
  useCreateCustomSection,
  useUpdateCustomSection,
  useDeleteCustomSection,
  useBulkDeleteCustomSections,
  customSectionSchema,
} from '@/app/features/custom-sections';
import type { CustomSectionFormData } from '@/app/features/custom-sections';

type CustomSection = {
  id: string;
  title: string;
  is_visible: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export function CustomSectionsPage() {
  const sections = useCustomSections();
  const createSection = useCreateCustomSection();
  const updateSection = useUpdateCustomSection();
  const deleteSection = useDeleteCustomSection();
  const bulkDeleteSections = useBulkDeleteCustomSections();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CustomSectionFormData>({
    title: '',
    is_visible: true,
  });
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof CustomSectionFormData, string>>
  >({});

  const unsaved = useUnsavedChanges(formData, { enabled: !!editingId });
  const editFormRef = useRef<HTMLDivElement>(null);

  const resetForm = useCallback(() => {
    setFormData({ title: '', is_visible: true });
    setFieldErrors({});
    setEditingId(null);
    setIsFormOpen(false);
    unsaved.clear();
  }, [unsaved]);

  function openCreateForm() {
    resetForm();
    setIsFormOpen(true);
  }

  function openEditForm(section: CustomSection) {
    unsaved.guardClose(() => {
      const data: CustomSectionFormData = {
        title: section.title,
        is_visible: section.is_visible,
      };
      setFormData(data);
      unsaved.snapshot(data);
      setFieldErrors({});
      setEditingId(section.id);
      setIsFormOpen(true);
      // Scroll the inline form into view and focus its first input.
      setTimeout(() => {
        editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        editFormRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
      }, 50);
    });
  }

  function handleCloseForm() {
    unsaved.guardClose(resetForm);
  }

  function handleChange(field: keyof CustomSectionFormData, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});

    const result = customSectionSchema.safeParse(formData);
    if (!result.success) {
      const errors: Partial<Record<keyof CustomSectionFormData, string>> = {};
      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof CustomSectionFormData;
        if (!errors[field]) {
          errors[field] = issue.message;
        }
      }
      setFieldErrors(errors);
      return;
    }

    const onSuccess = () => {
      resetForm();
      toast.success('Section saved successfully.');
    };

    if (editingId) {
      updateSection.mutate(
        { ...result.data, id: editingId },
        { onSuccess },
      );
    } else {
      createSection.mutate(result.data, { onSuccess });
    }
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteSection.mutate(id, {
      onSuccess: () => {
        setDeletingId(null);
        if (editingId === id) resetForm();
      },
      onError: () => setDeletingId(null),
    });
  }

  function handleToggleVisibility(section: CustomSection) {
    updateSection.mutate({
      id: section.id,
      is_visible: !section.is_visible,
    });
  }

  const isPending = createSection.isPending || updateSection.isPending;

  const sectionForm = (
    <FormCard className={editingId ? 'mt-0 sm:mt-0' : undefined}>
      <div className="flex items-center justify-between">
        <h2 className="text-[16px] font-semibold text-foreground">
          {editingId ? 'Edit Section' : 'New Section'}
        </h2>
        <button
          type="button"
          onClick={handleCloseForm}
          className="rounded-md p-2 sm:p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Close form"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {(createSection.error ?? updateSection.error) && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {(createSection.error ?? updateSection.error)?.message}
        </div>
      )}

      <form
        className="mt-4 space-y-4"
        onSubmit={handleSubmit}
        noValidate
      >
        {/* Section Title */}
        <div>
          <label
            htmlFor="cs-title"
            className="mb-1.5 block text-[14px] font-medium text-foreground"
          >
            Section Title
          </label>
          <input
            id="cs-title"
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="block w-full rounded-md border border-input bg-background px-3 py-2 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            placeholder="e.g. Projects, Languages, Publications, Awards..."
            aria-invalid={!!fieldErrors.title}
            aria-describedby={
              fieldErrors.title ? 'cs-title-error' : undefined
            }
          />
          {fieldErrors.title && (
            <p
              id="cs-title-error"
              className="mt-1 text-[13px] text-destructive"
            >
              {fieldErrors.title}
            </p>
          )}
        </div>

        {/* Visibility toggle */}
        <label className="flex items-center gap-2 text-[14px] text-foreground">
          <input
            type="checkbox"
            checked={formData.is_visible ?? true}
            onChange={(e) => handleChange('is_visible', e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
          />
          Include in generated CVs
        </label>

        <div className="flex items-center gap-3 border-t border-border pt-4">
          <button
            type="submit"
            disabled={isPending || (!!editingId && !unsaved.hasChanges)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-[15px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {editingId ? 'Update' : 'Save'}
          </button>
          <button
            type="button"
            onClick={handleCloseForm}
            className="rounded-md px-4 py-2 text-[15px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </FormCard>
  );

  const entries = (sections.data ?? []) as CustomSection[];

  const allIds = useMemo(() => entries.map((e) => e.id), [entries]);
  const bulk = useBulkSelection(allIds);
  const inSelectionMode = !bulk.noneSelected;

  const bulkActions: BulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        label: 'Delete',
        icon: Trash2,
        destructive: true,
        onExecute: async (ids) => {
          await bulkDeleteSections.mutateAsync(ids);
        },
      },
    ],
    [bulkDeleteSections],
  );

  if (sections.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (sections.error) {
    return (
      <div>
        <PageHeader title="Custom Sections" />
        <div
          className="mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          Failed to load custom sections. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Custom Sections"
        description="Create custom CV sections like Projects, Languages, Publications, Awards, or Volunteer Work."
      >
        {!isFormOpen && (
          <button
            type="button"
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-[14px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <Plus className="h-4 w-4" />
            Add Section
          </button>
        )}
      </PageHeader>

      {/* Bulk action bar */}
      {entries.length > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={bulk.allSelected}
              ref={(el) => {
                if (el) el.indeterminate = bulk.someSelected;
              }}
              onChange={bulk.toggleAll}
              className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-foreground"
            />
            {inSelectionMode
              ? `${bulk.count} of ${entries.length}`
              : 'Select all'}
          </label>
          <BulkActionBar
            selectedCount={bulk.count}
            selectedIds={bulk.selectedArray}
            actions={bulkActions}
            onDeselectAll={bulk.deselectAll}
          />
        </div>
      )}

      {/* Create form — the edit form renders inline in place of the section being edited */}
      {isFormOpen && !editingId && sectionForm}

      {/* Sections list */}
      {entries.length === 0 && !isFormOpen ? (
        <div className="mt-6 rounded-lg border border-dashed border-border p-12 text-center">
          <Layers className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-[15px] text-muted-foreground">
            No custom sections yet. Create sections like Projects, Languages,
            Publications, or Awards.
          </p>
          <button
            type="button"
            onClick={openCreateForm}
            className="mt-3 inline-flex items-center gap-1.5 text-[14px] font-medium text-primary transition-colors hover:text-primary/80"
          >
            <Plus className="h-3.5 w-3.5" />
            Create your first section
          </button>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {entries.map((section) =>
            editingId === section.id ? (
              <div key={section.id} ref={editFormRef}>{sectionForm}</div>
            ) : (
              <SectionCard
                key={section.id}
                section={section}
                onEdit={openEditForm}
                onDelete={handleDelete}
                onToggleVisibility={handleToggleVisibility}
                deletingId={deletingId}
                selected={bulk.isSelected(section.id)}
                onToggleSelect={() => bulk.toggle(section.id)}
                inSelectionMode={inSelectionMode}
              />
            ),
          )}
        </div>
      )}

      {deleteSection.error && (
        <div
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-[14px] text-destructive"
          role="alert"
        >
          {deleteSection.error.message}
        </div>
      )}

      <UnsavedChangesGuard {...unsaved.dialogProps} />
    </div>
  );
}

function SectionCard({
  section,
  onEdit,
  onDelete,
  onToggleVisibility,
  deletingId,
  selected,
  onToggleSelect,
  inSelectionMode,
}: {
  section: CustomSection;
  onEdit: (section: CustomSection) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (section: CustomSection) => void;
  deletingId: string | null;
  selected: boolean;
  onToggleSelect: () => void;
  inSelectionMode: boolean;
}) {
  return (
    <div
      className={`group rounded-lg border bg-card p-4 transition-colors hover:border-border/80 ${
        selected ? 'border-primary/40 bg-primary/[0.02]' : 'border-border'
      }`}
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <SelectionCheckbox
            checked={selected}
            onChange={onToggleSelect}
            visible={inSelectionMode}
            ariaLabel={`Select ${section.title}`}
          />
          <Link
            to="/custom-sections/$sectionId"
            params={{ sectionId: section.id }}
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md transition-colors hover:bg-muted/50 -m-2 p-2"
          >
            <div className="min-w-0 flex-1">
              <h3 className="text-[15px] font-semibold text-foreground">
                {section.title}
              </h3>
              <p className="mt-0.5 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                {section.is_visible ? (
                  <>
                    <Eye className="h-3 w-3" />
                    Visible in CVs
                  </>
                ) : (
                  <>
                    <EyeOff className="h-3 w-3" />
                    Hidden from CVs
                  </>
                )}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={() => onToggleVisibility(section)}
            className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={section.is_visible ? 'Hide from CVs' : 'Show in CVs'}
            title={section.is_visible ? 'Hide from CVs' : 'Show in CVs'}
          >
            {section.is_visible ? (
              <EyeOff className="h-3.5 w-3.5" />
            ) : (
              <Eye className="h-3.5 w-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => onEdit(section)}
            className="rounded-md p-2.5 sm:p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            aria-label={`Edit ${section.title}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <InlineDeleteConfirm
            onConfirm={() => onDelete(section.id)}
            isDeleting={deletingId === section.id}
            ariaLabel={`Delete ${section.title}`}
          />
        </div>
      </div>
    </div>
  );
}
